/**
 * The local calendar day of an instant.
 *
 * The server stores timestamps as ISO-8601 UTC, so `iso.slice(0, 10)` is the UTC day — which for
 * anyone east of Greenwich in the evening is tomorrow. A scrapbook that dates last night's date
 * card as today is a scrapbook that is quietly wrong about the story, so anything the app shows a
 * person as "the day this happened" goes through here. Server-computed `day` columns are already
 * local (they are cut at the edge with the couple's timezone) and must NOT be passed through this.
 */
export function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Short weekday and month names, hand-held rather than asked of `Intl`.
 *
 *  `Intl.DateTimeFormat` is present on the web and on modern Hermes builds and absent on older
 *  ones, and a date that renders as "Wed 3 Sep" on a laptop and throws on somebody's phone is the
 *  worst of the three outcomes. Twelve strings cost nothing and behave identically everywhere.
 *  en-GB order — "Wed 3 Sep", the way the rest of this app's copy is written. */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * A LOCAL CALENDAR DAY, as a person would say it: `Today`, `Yesterday`, then `Wed 3 Sep`.
 *
 * One helper, used by every list in the app that shows when something happened — Thanks' entries,
 * the scrapbook's detail line, a scratched card's day. They were printing raw `2026-09-02`, which
 * is a database column read out loud: correct, unambiguous, and not how anybody says what day it
 * was. The two relative labels cover the days somebody actually remembers, and everything older
 * gets the weekday, because "was that a weekend?" is the next question after "when".
 *
 * Takes a LOCAL day string (`YYYY-MM-DD`) — the shape the server's own day columns carry, already
 * cut with the couple's timezone. For an ISO timestamp, pass it through `localDay` first: doing
 * both here would mean guessing which of the two shapes arrived, and guessing wrong slips the
 * scrapbook a day for anyone east of Greenwich in the evening.
 *
 * The year is deliberately absent. This app's lists are weeks and months long, the day of the week
 * is what places a memory, and a "2026" on every row is a column of the same four characters.
 */
export function dayLabel(day: string, now: Date = new Date()): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!parts) return day;
  const [y, m, d] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  // Constructed at local midnight rather than parsed: `new Date('2026-09-03')` is UTC midnight,
  // which is the previous evening west of Greenwich — the exact bug `localDay` exists to avoid.
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return day;

  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysAgo = Math.round((midnight.getTime() - date.getTime()) / 86_400_000);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
}

/** Small counts spelled out, because these land MID-SENTENCE and a digit in prose is a receipt. */
const COUNTS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven'] as const;
const spell = (n: number): string => COUNTS[n] ?? String(n);

/**
 * HOW LONG AGO, as a clause you can drop into a sentence: `three weeks ago`, `last month`.
 *
 * `dayLabel`'s sibling and deliberately not its replacement — the two answer different questions.
 * `dayLabel` answers WHICH DAY, for a row in a list, and is a capitalised label. This one answers
 * HOW LONG, for the middle of a line of prose, and so it is lower case and carries no weekday: the
 * Pulse landing card asks about a plan that finished a fortnight ago, and "your request from
 * 2026-08-07 finished two weeks ago" made a reader do subtraction to understand half its own
 * sentence. Nobody remembers the date they wrote something; they remember it was a while back.
 *
 * Precision falls off the way memory does — days, then weeks, then months — because a card that
 * says "forty-one days ago" is being accurate about a thing nobody asked it to be accurate about.
 * Takes a LOCAL day string, same as `dayLabel`; run an ISO timestamp through `localDay` first.
 */
export function agoLabel(day: string, now: Date = new Date()): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!parts) return day;
  const date = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  if (Number.isNaN(date.getTime())) return day;

  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((midnight.getTime() - date.getTime()) / 86_400_000);
  // A clock skew or a day cut the other side of midnight reads as today rather than as the future.
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${spell(days)} days ago`;
  if (days < 45) {
    const weeks = Math.round(days / 7);
    return weeks <= 1 ? 'last week' : `${spell(weeks)} weeks ago`;
  }
  const months = Math.round(days / 30.44);
  if (months >= 12) return 'over a year ago';
  return months <= 1 ? 'last month' : `${spell(months)} months ago`;
}
